/**
 * 自定义钩子函数
 * 以 use 为开头，这里的所有方法必须要加上注释
 * */

import { ref, computed, onUnmounted, onMounted } from 'vue'
import { useRoute, useRouter, RouteLocationRaw } from 'vue-router'
import { getUserById } from '@/request/api/user'
import { useStoreHooks } from '@/store/hook'
import { Modal } from '@/components/common'
import { CERTIFICATION_TYPE } from '@/utils/config'
import { useI18n } from 'vue-i18n'

/**
 * @description 返回一个倒计时相关信息
 * @param {number} deadline 倒数计时的时间戳
 * @param {function} onTimeover 倒数计时回调
 */
export function useCountdown () {
  let timer: NodeJS.Timer
  const deadline = ref(Date.now())
  const now = ref(Date.now())
  const days = computed(() => Math.floor((deadline.value - now.value) / 1000 / 60 / 60 / 24))
  const hours = computed(() => Math.floor((deadline.value - now.value) / 1000 / 60 / 60) - days.value * 24)
  const minutes = computed(() => Math.floor((deadline.value - now.value) / 1000 / 60) - days.value * 24 * 60 - hours.value * 60)
  const seconds = computed(() => Math.floor((deadline.value - now.value) / 1000) - days.value * 24 * 60 * 60 - hours.value * 60 * 60 - minutes.value * 60)
  const countdown = (ddl: number, onTimeover?: () => void) => {
    deadline.value = ddl
    timer = setInterval(() => {
      if (deadline.value - now.value <= 1000) {
        clearInterval(timer)
        console.log('on countdown over!')
        // 倒计时结束，外部更新数据
        onTimeover?.()
      } else {
        now.value = Date.now()
      }
    }, 1000)
  }
  onUnmounted(() => {
    clearInterval(timer)
  })
  const getFormatTime = (time: number) => time < 10 && time >= 0 ? `0${time}` : time
  return {
    countdown,
    days: computed(() => getFormatTime(days.value)),
    hours: computed(() => getFormatTime(hours.value)),
    minutes: computed(() => getFormatTime(minutes.value)),
    seconds: computed(() => getFormatTime(seconds.value))
  }
}

/**
 * @description 返回pc/mobile端兼容的代码
 * */
export function useClientCompatibleHook () {
  const mobilePrefix = '/mobile'
  const route = useRoute()
  const router = useRouter()
  const isMobile = computed(() => route.path.split('/')[1] === 'mobile')
  const push = (args: RouteLocationRaw) => {
    let target = args
    if (isMobile.value) {
      if (typeof target === 'string' && !target.includes(mobilePrefix)) {
        target = `${mobilePrefix}${target}`
      } else if (Object.prototype.hasOwnProperty.call(target, 'path')) {
        target = target as { path: string }
        target.path = !target.path.includes(mobilePrefix) ? `${mobilePrefix}${target.path}` : target.path
      }
    }
    router.push(target)
  }
  return {
    isMobile,
    push
  }
}

/**
 * @description 返回定时更新用户token的钩子
 * 由于用户不操作时token30分钟过期，有些场景需要手动更新用户token，以免过期
*/
export function useUpdateTokenHook () {
  let timer = null as NodeJS.Timer | null
  const { state } = useStoreHooks()
  const initUpdateTimer = () => {
    // 10分钟更新一次用户的token
    onMounted(() => {
      console.log('on mounted')
      timer = setInterval(() => {
        getUserById(state.global.userInfo?.id + '' || '')
      }, 1000 * 60 * 10)
    })
    onUnmounted(() => {
      console.log('on unmounted')
      clearInterval(timer as NodeJS.Timer)
    })
  }
  return {
    initUpdateTimer
  }
}

/**
 * @description 特定站点用户报名时的提示
*/
export function useEnrollModalHook () {
  const onEnroll = (onHandleConfirm: () => void, kind = CERTIFICATION_TYPE.hsc) => {
    onHandleConfirm()
  }
  return {
    onEnroll
  }
}

/** /
 * @description modal需要支持i18n，需要以hooks的方式在setup中使用，不要再直接使用的confirm方法
*/
export function useModalConfirmHook () {
  const { t } = useI18n()
  const modalConfirm = (props: Record<string, unknown>) => {
    Modal.confirm({
      ...props,
      title: t('Tips'),
      okText: props.okText || t('Ok'),
      cancelText: props.cancelText || t('Cancel')
    })
  }
  return {
    confirm: modalConfirm
  }
}

/** /
 * @description seo相关配置应用
*/
export function useSeoConfigHook (path: string) {
  const { state: _state } = useStoreHooks()
  // 使用后台配置的seo项
  const seoConfigList = _state.global.seoConfig
  const seoItem = seoConfigList.find(i => i.url === path || `${i.url}/` === path)
  let title = seoItem?.title || _state.global.defaultPageTitle
  let description = seoItem?.description || ''
  if (path.includes('/certification/detail')) {
    // 认证详情页面
    title = _state.certification.detail.certificationDetail?.certificationName
    description = _state.certification.detail.certificationDetail?.description
  }
  if (path.includes('/course/detail')) {
    // 课程页面
    title = _state.course.detail.courseDetail?.courseName
    description = _state.course.detail.courseDetail?.description
  }
  return {
    title,
    description
  }
}
